import styled, {css, system, SystemProps, th} from 'quickstart/system'
import {cssVars, Font} from 'quickstart/theme/base/fonts'
import {Rhythm, Variant} from 'quickstart/theme/base/typography'

interface TextProps {
  $variant: Variant
  $proseFont: Font
  $font?: Font
  $rhythm?: Rhythm
  $bold?: boolean
  $italic?: boolean
  $underline?: boolean
  $lines?: number
}

interface ProseProps {
  $fonts?: 'prose' | 'ui'
}

export const Prose = styled.div.attrs(({$fonts = 'prose'}) => ({
  'data-fonts': $fonts,
  fonts: undefined,
}))<ProseProps & SystemProps>`
  ${system}
`

const lineClamp = (lines: number) => css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines || 'none'};
  overflow: hidden;
`

const fontWeightBold = css`
  font-weight: var(${cssVars.weights.bold});
`

const fontStyleItalic = css`
  font-style: italic;
`

export const Text = styled.div<Omit<SystemProps, 'variant'> & TextProps>(
  ({
    $font,
    $proseFont,
    $variant,
    $rhythm,
    $bold,
    $italic,
    $underline,
    $lines,
  }) => css`
    /* Reset, since we don't have a global reset. */
    margin: 0;
    padding: 0;

    ${$font
      ? th(`fonts.${$font}`)
      : css`
          ${Prose as any}[data-fonts=prose] && {
            ${th(`fonts.${$proseFont}`)};
          }
        `};

    ${th(`ui.${$variant}`)};

    ${$rhythm === 'prose'
      ? th(`spaceAfter.${$variant}`)
      : $rhythm !== 'layout' &&
        css`
          ${Prose} && {
            ${th(`spaceAfter.${$variant}`)};
          }
          ${Prose} &&:last-child {
            margin-bottom: 0;
          }
          ${Prose} :not(:last-child) && {
            ${th(`spaceAfter.${$variant}`)};
          }
        `};

    ${$bold && fontWeightBold};
    strong {
      ${fontWeightBold};
    }

    ${$italic && fontStyleItalic};
    em {
      ${fontStyleItalic};
    }

    ${$underline && th('underline')};
    u {
      ${th('underline')};
    }

    ${!!$lines && $lines !== Infinity && lineClamp($lines)};

    ${system};
  `,
)
